import { GroupType } from "types/assessmentTypes";
import { Company } from "types/companyTypes";

const normalizeFiles = (value: any): any => {
  if (!value) return null;

  if (Array.isArray(value)) {
    if (value.length === 0) return [];

    if (typeof value[0] === "string") {
      return value.map((name) => ({ name }));
    }

    if (typeof value[0] === "object" && value[0]?.name) {
      return value;
    }

    return value;
  }

  if (typeof value === "string") {
    return { name: value };
  }

  if (typeof value === "object" && value?.name) {
    return value;
  }

  return value;
};

const normalizeDocuments = (documents: any): any => {
  if (!documents || typeof documents !== "object") return {};

  const result: any = {};

  const docFields = [
    "declaration",
    "lastInsuranceList",
    "patentLicense",
    "brandRegistrationLicense",
    "others",
  ];

  docFields.forEach((field) => {
    if (documents[field] !== undefined && documents[field] !== null) {
      result[field] = normalizeFiles(documents[field]);
    }
  });

  return result;
};

export const buildCompanyPayload = (
  values: Company,
  mode?: "create" | "edit" | "view"
) => {
  const production = Number(values?.totalStaffCount?.production || 0);
  const administrative = Number(values?.totalStaffCount?.administrative || 0);
  const total = production + administrative;
  let companySize = null;

  if (total >= 1 && total <= 10) {
    companySize = GroupType.MICRO;
  } else if (total > 10 && total < 50) {
    companySize = GroupType.SMALL;
  } else if (total >= 50 && total < 250) {
    companySize = GroupType.MEDIUM;
  } else if (total >= 250) {
    companySize = GroupType.LARGE;
  }

  const logo = normalizeFiles(values.logo);
  const organizationChartImage = normalizeFiles(values.organizationChartImage);
  const documents = normalizeDocuments(values.documents);

  const payload: any = {
    // basic
    name: values.name || "",
    logo: logo,
    userId: values.user?.id || values.userId || null,
    nationalId: values.nationalId || "",
    provinceId: values.provinceId || null,
    organizationId: values.organization?.id || values.organizationId || null,
    industrialId: values.industrial?.id || values.industrialId || null,

    // business
    industrySectorId: values.industrySectorId || null,
    industrySectorOther: values.industrySectorOther || null,
    activityType: values.activityType || null,
    activityDescription: values.activityDescription || null,
    companyType: values.companyType || null,
    companyTypeOther: values.companyTypeOther || null,
    foundationYear: values.foundationYear || null,
    licenseNumber: values.licenseNumber ? Number(values.licenseNumber) : null,
    licenseDate: values.licenseDate || null,
    isKnowledgeBased: values.isKnowledgeBased || false,
    knowledgeBasedYear: values.knowledgeBasedYear || null,
    companyStage: values.companyStage || null,
    isListed: values.isListed || false,
    hasExport: values.hasExport || false,
    industrialOther: values.industrialOther || null,
    isTechnologist: values.isTechnologist || false,
    technologistLicenseNumber: values.technologistLicenseNumber || null,
    technologistLicenseDate: values.technologistLicenseDate || null,

    // totalStaffCount
    totalStaffCount: {
      production,
      administrative,
      total,
      companySize,
    },

    // meta
    meta: {
      brandName: values.meta?.brandName || "",
      website: values.meta?.website || "",
      companyAddress: values.meta?.companyAddress || "",
      companyPhone: values.meta?.companyPhone || "",
      officeAddress: values.meta?.officeAddress || "",
      officePhone: values.meta?.officePhone || "",
      email: values.meta?.email || "",
      agentName: values.meta?.agentName || "",
      agentMobile: values.meta?.agentMobile || "",
      technologistLicenseRefrence:
        values.meta?.technologistLicenseRefrence || "",
    },

    // arrays and objects with normalized files
    organizationChartImage: organizationChartImage || [],
    products: values.products || [],
    customers: values.customers || [],
    suppliers: values.suppliers || [],
    processSteps: values.processSteps || [],
    documents: documents,
    people: values.people || {},
  };

  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined || payload[key] === null) {
      delete payload[key];
    }

    if (key === "documents" && Object.keys(payload[key]).length === 0) {
      delete payload[key];
    }
  });

  if (mode === "edit") {
    const forbiddenFields = [
      "id",
      "createdAt",
      "updatedAt",
      "province",
      "provinceName",
      "organizationName",
      "userName",
    ];
    forbiddenFields.forEach((field) => delete payload[field]);
  }

  return payload;
};

export const stepFields = {
  1: [
    "name",
    "nationalId",
    "industrialId",
    "meta.brandName",
    "organizationId",
    "provinceId",
    "meta.website",
    "meta.companyAddress",
    "meta.companyPhone",
    "meta.officeAddress",
    "meta.officePhone",
    "meta.email",
    "meta.agentName",
    "meta.agentMobile",
    "userId",
  ],

  2: [
    "industrySectorId",
    "industrySectorOther",
    "activityType",
    "activityDescription",
    "companyType",
    "companyTypeOther",
    "licenseNumber",
    "licenseDate",
    "companyStage",
    "totalStaffCount",
    "foundationYear",
    "isKnowledgeBased",
    "knowledgeBasedYear",
    "isListed",
    "hasExport",
    "isTechnologist",
    "technologistLicenseNumber",
    "technologistLicenseDate",
    "meta.technologistLicenseRefrence",
  ],

  3: ["customers", "suppliers", "processSteps"],

  4: [
    "logo",
    "organizationChartImage",
    "documents.declaration",
    "documents.lastInsuranceList",
    "documents.patentLicense",
    "documents.brandRegistrationLicense",
    "documents.others",
  ],
};
